/**
 * Favorites and the home try-on list.
 *
 * Both lists are kept in the visitor's browser (localStorage), so they work
 * without an account or an app. Buttons with [data-list-toggle] add or
 * remove a product; elements with [data-list-count] show how many are
 * saved; a [data-list-page] element renders the saved products.
 */
(() => {
  const PREFIX = 'yoroi:list:';
  const memory = {};

  const read = (name) => {
    try {
      return JSON.parse(window.localStorage.getItem(PREFIX + name)) || [];
    } catch (error) {
      return memory[name] || [];
    }
  };

  const write = (name, items) => {
    memory[name] = items;
    try {
      window.localStorage.setItem(PREFIX + name, JSON.stringify(items));
    } catch (error) {
      /* Private browsing: the list lasts for this page only. */
    }
  };

  const maxFor = (name) => {
    if (name !== 'tryon') return 100;
    return Number(document.body.dataset.tryonMax) || 3;
  };

  const announce = (message) => {
    const region = document.querySelector('[data-list-status]');
    if (region) region.textContent = message;
  };

  const sync = () => {
    ['favorites', 'tryon'].forEach((name) => {
      const items = read(name);
      const handles = items.map((item) => item.handle);

      document.querySelectorAll(`[data-list-toggle="${name}"]`).forEach((button) => {
        const saved = handles.includes(button.dataset.handle);
        const text = saved ? button.dataset.labelRemove : button.dataset.labelAdd;
        button.setAttribute('aria-pressed', String(saved));
        const label = button.querySelector('[data-list-label]');
        if (label) label.textContent = text;
        else button.setAttribute('aria-label', text);
      });

      document.querySelectorAll(`[data-list-count="${name}"]`).forEach((count) => {
        count.textContent = String(items.length);
        const wrapper = count.closest('[data-list-count-wrapper]');
        if (wrapper) wrapper.hidden = items.length === 0;
      });
    });

    document.querySelectorAll('[data-list-page]').forEach(renderPage);
  };

  const selectedColor = (button) => {
    const product = button.closest('product-info');
    if (!product) return '';
    const input = product.querySelector('[data-option-color] [data-option-input]:checked');
    return input ? input.value : '';
  };

  const selectedImage = (button) => {
    const product = button.closest('product-info');
    const image = product && product.querySelector('.gallery__item.is-active img');
    return image ? image.currentSrc || image.src : '';
  };

  const toggle = (button) => {
    const name = button.dataset.listToggle;
    const items = read(name);
    const index = items.findIndex((item) => item.handle === button.dataset.handle);

    if (index > -1) {
      items.splice(index, 1);
    } else {
      if (items.length >= maxFor(name)) {
        announce(document.body.dataset.tryonFull || '');
        const status = button.parentElement.querySelector('.tryon__status');
        if (status) status.classList.add('is-full');
        return;
      }
      items.push({
        handle: button.dataset.handle,
        title: button.dataset.title,
        vendor: button.dataset.vendor,
        url: button.dataset.url,
        image: selectedImage(button) || button.dataset.image,
        price: button.dataset.listPrice,
        color: selectedColor(button),
      });
    }

    write(name, items);
    sync();
  };

  const renderPage = (page) => {
    const name = page.dataset.listPage;
    const items = read(name);
    const grid = page.querySelector('[data-list-items]');
    const template = page.querySelector('template');
    const empty = page.querySelector('[data-list-empty]');
    const filled = page.querySelector('[data-list-filled]');

    grid.textContent = '';
    items.forEach((item) => {
      const node = template.content.cloneNode(true);
      const link = node.querySelector('[data-item-link]');
      const image = node.querySelector('[data-item-image]');
      link.href = item.url;
      link.textContent = item.title;
      node.querySelector('[data-item-media]').href = item.url;
      if (item.image) {
        image.src = item.image;
      } else {
        image.remove();
      }
      node.querySelector('[data-item-vendor]').textContent = item.vendor || '';
      node.querySelector('[data-item-color]').textContent = item.color || '';
      node.querySelector('[data-item-price]').textContent = item.price || '';
      node.querySelector('[data-item-remove]').dataset.handle = item.handle;
      grid.appendChild(node);
    });

    if (empty) empty.hidden = items.length > 0;
    if (filled) filled.hidden = items.length === 0;

    const properties = page.querySelector('[data-tryon-properties]');
    if (properties) {
      properties.textContent = '';
      items.forEach((item, index) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${properties.dataset.label} ${index + 1}]`;
        input.value = item.color ? `${item.title}（${item.color}）` : item.title;
        properties.appendChild(input);
      });
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-list-toggle]');
    if (button) {
      event.preventDefault();
      toggle(button);
      return;
    }

    const remove = event.target.closest('[data-item-remove]');
    if (remove) {
      const page = remove.closest('[data-list-page]');
      const name = page.dataset.listPage;
      write(
        name,
        read(name).filter((item) => item.handle !== remove.dataset.handle)
      );
      sync();
    }
  });

  document.addEventListener('yoroi:content-added', sync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
